import { GroupChannel } from '@sendbird/chat/groupChannel';
import { OpenChannel } from '@sendbird/chat/openChannel';
import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { useNotify, useTranslate } from 'ra-core';
import { type SendableMessage } from '../../../resources/Chat/ForwardMessageProvider';
import { MAX_PINNED_MESSAGES } from './constants';

type PinUnpinMessageParams = {
  message: SendableMessage;
  channel: GroupChannel | OpenChannel;
  isPinned: boolean | undefined;
};

const pinOrUnpinMessage = async ({
  message,
  channel,
  isPinned,
}: PinUnpinMessageParams) => {
  if (isPinned) {
    await channel.unpinMessage(message.messageId);
  } else {
    await channel.pinMessage(message.messageId);
  }
};

export const usePinUnpinMessage = (
  options?: UseMutationOptions<void, Error, PinUnpinMessageParams>
) => {
  const notify = useNotify();
  const translate = useTranslate();
  const queryClient = useQueryClient();

  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  return useMutation<void, Error, PinUnpinMessageParams>({
    mutationKey: ['pinOrUnpinMessage'],
    mutationFn: pinOrUnpinMessage,
    ...options,
    onError: () => {
      notify(translate('chat.somethingWentWrong'), {
        type: 'error',
        autoHideDuration: 5000,
      });
    },
    onSuccess: async (_data, variable) => {
      if (variable.isPinned) {
        notify(translate('chat.unpinMessage'), { type: 'success' });
      } else {
        notify(
          translate('chat.pinMessage', {
            maxPinnedMessages: MAX_PINNED_MESSAGES,
          }),
          { type: 'success' }
        );
      }

      await queryClient.invalidateQueries({
        queryKey: ['pinned-messages', variable.channel.url],
      });
    },
  });
};
